import React from 'react';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';

import { Button, Icon } from '@components/ui';
import * as OrderHelpers from '@helpers/OrderHelpers';
import * as ProductHelpers from '@helpers/ProductHelpers';
import { useAnalytics } from '@hooks/useAnalytics';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useConfig } from '@hooks/data/useConfig';
import { useOrderProductMutation } from '@hooks/data/useOrderProductMutation';

import ProductQuantityEdit from '../ProductQuantityEdit/ProductQuantityEdit';

import type {
  IAnalyticsListProps,
  IOrder,
  IProduct
} from 'types';

import styles from './ProductSearchCardActions.module.scss';

interface Props {
  className?: string,
  //
  order: IOrder,
  product: IProduct,
  listProps: IAnalyticsListProps,
  quantity: number,
  //
  onLoading: (isLoading: boolean) => void,
  onSimilarClick: (e: React.MouseEvent<HTMLButtonElement>) => void,
  onQuantityChange?: (data: IOrder) => void
}

const ProductSearchCardActions = (props: Props) => {
  const {
    className,
    //
    order,
    product,
    listProps = {},
    quantity,
    //
    onLoading = () => {},
    onSimilarClick = () => {},
    onQuantityChange: onQuantityChangeSuccess = () => {},
  } = props;

  // Hooks

  const analytics = useAnalytics();

  const { t } = useTranslation();
  const { data: config } = useConfig();
  const { errors } = config || {};

  const [isLoading, setIsLoading] = React.useState(false);

  const {
    isError: isMutationError, add: addProduct, edit: editProductQuantity, remove: removeProduct,
  } = useOrderProductMutation();

  // Props

  const orderItem = React.useMemo(() => OrderHelpers.getOrderItem(order, product), [order, product]);
  const [maxQuantity] = React.useMemo(() => ProductHelpers.getMaxAllowedQuantity(orderItem, errors), [orderItem, errors]);

  const targetQuantity = quantity > maxQuantity ? maxQuantity : null;

  const { isAvailable } = product || {};
  const { isOk } = orderItem || {};

  const { tokenValue: orderTokenValue } = order || {};

  const { id: orderItemId } = orderItem || {};

  //  Effects

  React.useEffect(() => {
    onLoading(isLoading);
  }, [isLoading]);

  // Handlers

  // --- Add

  const handleAdd = React.useCallback((e) => {
    e.preventDefault();

    setIsLoading(true);

    addProduct({
      product,
      listProps,
      order,
      bypassAddressRequirement: true,
      onSuccess: (data: IOrder) => {
        ProductHelpers.checkMaxQuantityReached(0, 1, product, errors, { analytics });

        if (onQuantityChangeSuccess) {
          onQuantityChangeSuccess(data);
        }
      },
      onError: () => {
      },
      onSettled: () => {
        setIsLoading(false);
      }
    });
  }, [order, orderItem, product]);

  // --- Edit

  const handleQuantityEdit = React.useCallback((oldQuantity, newQuantity) => {
    setIsLoading(true);

    editProductQuantity({
      product,
      order: {
        tokenValue: orderTokenValue
      },
      orderItem: {
        id: orderItemId
      },
      listProps,
      quantity: newQuantity,
      previousQuantity: oldQuantity,
      onSuccess: (data: IOrder) => {
        setIsLoading(false);

        ProductHelpers.checkMaxQuantityReached(oldQuantity, newQuantity, product, errors, { analytics });

        if (onQuantityChangeSuccess) {
          onQuantityChangeSuccess(data);
        }
      },
      onError: () => {
        setIsLoading(false);
      },
      onSettled: () => {}
    });
  }, [orderTokenValue, orderItemId, product]);

  // --- Remove

  const handleRemove = React.useCallback(() => {
    setIsLoading(true);

    removeProduct({
      product,
      order: {
        tokenValue: orderTokenValue
      },
      orderItem: {
        id: orderItemId
      },
      listProps,
      onSuccess: (data: IOrder) => {
        setIsLoading(false);

        if (onQuantityChangeSuccess) {
          onQuantityChangeSuccess(data);
        }
      },
      onError: () => {
        setIsLoading(false);
      }
    });
  }, [orderTokenValue, orderItemId, product]);

  // --- Quantity Edit

  const onQuantityChange = React.useCallback((oldQuantity, newQuantity) => {
    ProductHelpers.handleChangeQuantityClick(
      oldQuantity,
      newQuantity,
      handleRemove,
      handleQuantityEdit
    );
  }, [handleRemove, handleQuantityEdit]);

  // --- Click

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Not available

  if (!isAvailable) {
    return (
      <div
        className={classnames(
          styles.root,
          className
        )}
        onClick={onClick}
      >
        <Button
          className={styles.viewSimilarBtn}
          size="medium"
          onClick={onSimilarClick}
        >
          {t('PRODUCT.SIMILAR_PRODUCTS')}
        </Button>
      </div>
    );
  }

  // Render

  return (
    <div
      className={classnames(
        styles.root,
        className
      )}
      onClick={onClick}
    >
      {
        quantity === 0
          ? (
            <>
              <Button
                className={classnames(styles.addBtn, { [styles.disabled]: isLoading })}
                size="small"
                variant="secondary"
                startIcon={CartIcon}
                disabled={isLoading}
                onClick={handleAdd}
              >
                {t('ACTIONS.ADD')}
              </Button>
            </>
          ) : (
            !isOk
              ? (
                targetQuantity && (
                  <>
                    {/* Adjust quantity */}
                    <Button
                      className={classnames(styles.adjustBtn, { [styles.disabled]: isLoading })}
                      variant="secondary"
                      size="small"
                      disabled={isLoading}
                      onClick={() => onQuantityChange(quantity, targetQuantity)}
                    >
                      {`${t('ACTIONS.ADD')} ${targetQuantity}`}
                    </Button>
                  </>
                )
              ) : (
                <>
                  {/* Quantity Edit */}
                  <ProductQuantityEdit
                    className={styles.actions}
                    decreaseButtonClassName={styles.decreaseBtn}
                    inputClassName={styles.quantityInput}
                    product={product}
                    quantity={quantity}
                    expanded
                    isMutationError={isMutationError}
                    onQuantityChange={onQuantityChange}
                  />
                </>
              )
          )
      }
    </div>
  );
};

// Icons

const CartIcon = <Icon name="cart-plus" size={20} />;

// Export

export default React.memo(ProductSearchCardActions);
